import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Paper,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import TuneIcon from '@mui/icons-material/Tune';
import axios from 'axios';
import TimerCard, {
  Timer,
  TimerRecurrence,
  TimerState,
  formatRecurrence,
  formatTimerDuration,
  getTimerDisplayTarget,
} from '../components/TimerCard';
import AddTimerDialog, { TimerFormData } from '../components/AddTimerDialog';
import CollapsibleFilterBar from '../components/CollapsibleFilterBar';
import DurationDisplayDialog from '../components/DurationDisplayDialog';
import TimerViewsBar from '../components/TimerViewsBar';
import { getApiErrorMessage } from '../utils/apiError';
import PageShell from '../components/PageShell';
import ViewModeToggle from '../components/ViewModeToggle';
import {
  LifeEventsDisplaySettings,
  SavedTimerView,
  TagFilterMode,
  useLifeEventsDisplaySettings,
  usePreferences,
} from '../contexts/PreferencesContext';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { usePersistentViewMode } from '../hooks/usePersistentViewMode';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type SortBy = 'created_at' | 'updated_at' | 'next_due' | 'duration' | 'title' | 'priority';
type SortOrder = 'asc' | 'desc';

interface TimerTagOption {
  id: number;
  name: string;
  usage_count: number;
}

const recurrenceOptions: Array<{ value: TimerRecurrence | 'sub-daily'; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' },
  { value: 'sub-daily', label: 'Sub-daily' },
];

// "archived" is one more member of the open state list (saved views store
// TimerViewFilters.state as string[]), so archived-state views compose with
// the existing machinery. The server hides archived timers unless the state
// filter asks for them.
const stateOptions: Array<{ value: TimerState; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'expired', label: 'Expired' },
  { value: 'completed', label: 'Completed' },
  { value: 'not_started', label: 'Not started' },
  { value: 'archived', label: 'Archived' },
];

const sortOptions: Array<{ value: SortBy; label: string }> = [
  { value: 'created_at', label: 'Created date' },
  { value: 'updated_at', label: 'Updated date' },
  { value: 'next_due', label: 'Next due date' },
  { value: 'duration', label: 'Duration' },
  { value: 'title', label: 'Name' },
  { value: 'priority', label: 'Priority tag' },
];

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const stateColor = (state?: string) => {
  if (state === 'active') return 'success';
  if (state === 'paused' || state === 'archived') return 'default';
  if (state === 'expired' || state === 'completed') return 'warning';
  return 'info';
};

const parseMultiSelect = (value: string | string[]): string[] => (
  typeof value === 'string' ? value.split(',').filter(Boolean) : value
);

const sortTagNames = (names: string[]): string[] =>
  [...names].sort((a, b) => {
    const priority = (tag: string) => (/^p\d+$/.test(tag) ? Number(tag.slice(1)) : 1000);
    return priority(a) - priority(b) || a.localeCompare(b);
  });

const normalizeSortField = (field: string): SortBy =>
  sortOptions.some((option) => option.value === field)
    ? (field as SortBy)
    : 'created_at';

/**
 * The slice of on-screen state a saved view captures. Date and duration
 * filters are outside the view system (the ratified schema has no fields for
 * them); applying a view clears them so the view alone defines the query.
 */
interface ViewSignature {
  tags: string[];
  tag_mode: TagFilterMode;
  recurrence_types: string[];
  state: string[];
  search: string;
  sort_field: SortBy;
  sort_direction: SortOrder;
}

const signatureFromView = (view: SavedTimerView): ViewSignature => ({
  tags: view.filters.tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean).sort(),
  tag_mode: view.filters.tag_mode,
  recurrence_types: [...view.filters.recurrence_types].sort(),
  state: [...view.filters.state].sort(),
  search: view.filters.search.trim(),
  sort_field: normalizeSortField(view.sort.field),
  sort_direction: view.sort.direction,
});

const matchesTagFilter = (
  timer: Timer,
  wanted: string[],
  mode: TagFilterMode,
): boolean => {
  if (!wanted.length) return true;
  const carried = new Set(timer.tags.map((tag) => tag.toLowerCase()));
  return mode === 'all'
    ? wanted.every((tag) => carried.has(tag))
    : wanted.some((tag) => carried.has(tag));
};

const Timers: React.FC = () => {
  const {
    dialogContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    sectionGapSx,
    tableCellPaddingSx,
  } = useLayoutTokens();
  const store = usePreferences();
  const display = useLifeEventsDisplaySettings();
  const [timers, setTimers] = useState<Timer[]>([]);
  const [tagNames, setTagNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTimer, setEditTimer] = useState<Timer | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timerToDelete, setTimerToDelete] = useState<Timer | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [durationDialogOpen, setDurationDialogOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const [viewMode, setViewMode, viewModeControls] = usePersistentViewMode('timers');
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [recurrenceFilter, setRecurrenceFilter] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [olderThanDays, setOlderThanDays] = useState('');
  const [minDurationHours, setMinDurationHours] = useState('');
  const [maxDurationHours, setMaxDurationHours] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Lane L: Any/All multi-tag semantics. The account-level tag_filter_mode is
  // the default; an active saved view's tag_mode overrides it via this local
  // override (null = follow the account preference).
  const accountTagMode: TagFilterMode =
    store?.settings.tag_filter_mode === 'all' ? 'all' : 'any';
  const [tagModeOverride, setTagModeOverride] = useState<TagFilterMode | null>(null);
  const effectiveTagMode = tagModeOverride ?? accountTagMode;

  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const defaultViewAppliedRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchTagOptions = useCallback(async () => {
    try {
      const response = await axios.get<TimerTagOption[]>(`${API_BASE_URL}/api/timer-tags/`);
      setTagNames(response.data.map((tag) => tag.name));
    } catch {
      // Degrade gracefully: no suggestions, but filtering still works freeSolo.
      setTagNames([]);
    }
  }, []);

  const fetchTimers = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (search.trim()) params.search = search.trim();
      // The backend applies the ACCOUNT-level tag_filter_mode to the tags
      // param (there is no per-request mode). When a view overrides the mode
      // to 'any' while the account says 'all', sending tags would under-fetch,
      // so fetch unfiltered and match client-side instead. In every other
      // combination the server result is a superset (or exact), and the
      // client-side pass below settles the effective semantics.
      if (
        selectedTags.length
        && !(effectiveTagMode === 'any' && accountTagMode === 'all')
      ) {
        params.tags = selectedTags.join(',');
      }
      if (recurrenceFilter.length) params.recurrence = recurrenceFilter.join(',');
      if (stateFilter.length) params.state = stateFilter.join(',');
      if (createdAfter) params.created_after = new Date(`${createdAfter}T00:00:00`).toISOString();
      if (createdBefore) params.created_before = new Date(`${createdBefore}T23:59:59`).toISOString();
      if (olderThanDays) params.older_than_days = Number(olderThanDays);
      if (minDurationHours) params.min_duration_seconds = Math.round(Number(minDurationHours) * 3600);
      if (maxDurationHours) params.max_duration_seconds = Math.round(Number(maxDurationHours) * 3600);

      const response = await axios.get<Timer[]>(`${API_BASE_URL}/api/timers/`, { params });
      setTimers(
        response.data.filter((timer) =>
          matchesTagFilter(timer, selectedTags, effectiveTagMode),
        ),
      );
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch timers:', err);
      setError(getApiErrorMessage(err, 'Failed to load timers'));
    } finally {
      setLoading(false);
    }
  }, [
    search,
    selectedTags,
    effectiveTagMode,
    accountTagMode,
    recurrenceFilter,
    stateFilter,
    createdAfter,
    createdBefore,
    olderThanDays,
    minDurationHours,
    maxDurationHours,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchTimers();
  }, [fetchTimers]);

  useEffect(() => {
    fetchTagOptions();
  }, [fetchTagOptions]);

  const tagOptions = useMemo(() => sortTagNames(tagNames), [tagNames]);

  const activeFilterCount = [
    Boolean(search.trim()),
    selectedTags.length > 0,
    recurrenceFilter.length > 0,
    stateFilter.length > 0,
    Boolean(createdAfter),
    Boolean(createdBefore),
    Boolean(olderThanDays),
    Boolean(minDurationHours),
    Boolean(maxDurationHours),
  ].filter(Boolean).length;
  const hasFilters = activeFilterCount > 0;

  // -------------------------------------------------------------------------
  // Saved views — rule-based filter sets persisted in life_events_display.
  // The namespace is replaced wholesale on every write, so writeDisplay always
  // sends the complete object (preserving the duration_* fields and whatever
  // this write does not touch).
  // -------------------------------------------------------------------------

  const writeDisplay = useCallback(
    (patch: Partial<LifeEventsDisplaySettings>) => {
      if (!store) return;
      store.setNamespace(
        'life_events_display',
        { ...display, ...patch } as unknown as Record<string, unknown>,
      );
    },
    [store, display],
  );

  const applyView = useCallback(
    (view: SavedTimerView) => {
      setSearch(view.filters.search);
      setSelectedTags(
        view.filters.tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean),
      );
      setTagModeOverride(view.filters.tag_mode);
      setRecurrenceFilter([...view.filters.recurrence_types]);
      setStateFilter([...view.filters.state]);
      // Views define the whole filter set: dimensions the view schema does not
      // capture are cleared rather than left lurking under the new view.
      setCreatedAfter('');
      setCreatedBefore('');
      setOlderThanDays('');
      setMinDurationHours('');
      setMaxDurationHours('');
      setSortBy(normalizeSortField(view.sort.field));
      setSortOrder(view.sort.direction);
      if (view.view_mode) setViewMode(view.view_mode);
      setActiveViewId(view.id);
    },
    [setViewMode],
  );

  const activeView = useMemo(
    () => display.saved_views.find((view) => view.id === activeViewId) ?? null,
    [display.saved_views, activeViewId],
  );

  const currentSignature: ViewSignature = {
    tags: [...selectedTags].sort(),
    tag_mode: effectiveTagMode,
    recurrence_types: [...recurrenceFilter].sort(),
    state: [...stateFilter].sort(),
    search: search.trim(),
    sort_field: sortBy,
    sort_direction: sortOrder,
  };

  const viewModified = activeView
    ? JSON.stringify(currentSignature) !== JSON.stringify(signatureFromView(activeView))
      || (activeView.view_mode !== null && activeView.view_mode !== viewMode)
    : false;

  // The default view applies once on page load; default_view_id null means no
  // filters. Skipped if the user already touched filters before the settings
  // arrived (late hydration must not stomp on their input).
  useEffect(() => {
    if (defaultViewAppliedRef.current) return;
    if (!display.default_view_id) return;
    const view = display.saved_views.find(
      (candidate) => candidate.id === display.default_view_id,
    );
    if (!view) return;
    defaultViewAppliedRef.current = true;
    if (hasFilters || activeViewId) return;
    applyView(view);
  }, [display.default_view_id, display.saved_views, hasFilters, activeViewId, applyView]);

  const showAllTimers = () => {
    setSearch('');
    setSelectedTags([]);
    setRecurrenceFilter([]);
    setStateFilter([]);
    setCreatedAfter('');
    setCreatedBefore('');
    setOlderThanDays('');
    setMinDurationHours('');
    setMaxDurationHours('');
    setSortBy('created_at');
    setSortOrder('desc');
    setTagModeOverride(null);
    setActiveViewId(null);
  };

  const handleSaveViewAs = (name: string, makeDefault: boolean) => {
    const id = `view-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const view: SavedTimerView = {
      id,
      name,
      icon: null,
      filters: {
        tags: [...selectedTags],
        // A new view's tag mode inherits the account default.
        tag_mode: accountTagMode,
        recurrence_types: [...recurrenceFilter],
        recurrence_interval_min: null,
        recurrence_interval_max: null,
        timer_type: 'any',
        state: [...stateFilter],
        search: search.trim(),
      },
      sort: { field: sortBy, direction: sortOrder },
      view_mode: viewMode,
      columns: {},
    };
    writeDisplay({
      saved_views: [...display.saved_views, view],
      ...(makeDefault ? { default_view_id: id } : {}),
    });
    applyView(view);
  };

  const handleSaveViewChanges = () => {
    if (!activeView) return;
    const updated: SavedTimerView = {
      ...activeView,
      filters: {
        ...activeView.filters,
        tags: [...selectedTags],
        tag_mode: effectiveTagMode,
        recurrence_types: [...recurrenceFilter],
        state: [...stateFilter],
        search: search.trim(),
      },
      sort: { field: sortBy, direction: sortOrder },
      view_mode: activeView.view_mode === null ? null : viewMode,
    };
    writeDisplay({
      saved_views: display.saved_views.map((view) =>
        view.id === updated.id ? updated : view,
      ),
    });
  };

  const handleRevertView = () => {
    if (activeView) applyView(activeView);
  };

  const handleDeleteView = (view: SavedTimerView) => {
    writeDisplay({
      saved_views: display.saved_views.filter((candidate) => candidate.id !== view.id),
      default_view_id:
        display.default_view_id === view.id ? null : display.default_view_id,
    });
    // The on-screen filters stay put; only the view association is dropped.
    if (activeViewId === view.id) setActiveViewId(null);
  };

  const handleSetDefaultView = (view: SavedTimerView | null) => {
    writeDisplay({ default_view_id: view ? view.id : null });
  };

  const handleTagModeChange = (value: TagFilterMode | null) => {
    if (!value) return;
    if (activeViewId !== null) {
      // Editing a view's filter set: an override the user can Save or Revert.
      setTagModeOverride(value);
      return;
    }
    if (store) {
      // No view active: the control edits the account-level preference, which
      // the backend reads when applying the tags query param.
      store.setTyped('tag_filter_mode', value);
      setTagModeOverride(null);
    } else {
      setTagModeOverride(value);
    }
  };

  const refreshTimers = async () => {
    await Promise.all([fetchTimers(), fetchTagOptions()]);
  };

  const handleAddClick = () => {
    setEditTimer(null);
    setDialogOpen(true);
  };

  const handleEditClick = (timer: Timer) => {
    setEditTimer(timer);
    setDialogOpen(true);
  };

  const handleDeleteClick = (timer: Timer) => {
    setTimerToDelete(timer);
    setDeleteDialogOpen(true);
  };

  const handleSaveTimer = async (timerData: TimerFormData) => {
    const payload = {
      ...timerData,
      target_date: new Date(timerData.target_date).toISOString(),
      recurrence_anchor_date: timerData.recurrence_anchor_date
        ? new Date(timerData.recurrence_anchor_date).toISOString()
        : null,
    };

    if (editTimer) {
      await axios.put(`${API_BASE_URL}/api/timers/${editTimer.id}`, payload);
    } else {
      await axios.post(`${API_BASE_URL}/api/timers/`, payload);
    }
    await refreshTimers();
  };

  const handleConfirmDelete = async () => {
    if (!timerToDelete) return;

    setDeleteLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/timers/${timerToDelete.id}`);
      await refreshTimers();
      setDeleteDialogOpen(false);
      setTimerToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete timer:', err);
      setError(getApiErrorMessage(err, 'Failed to delete timer'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const updateTimerActive = async (timer: Timer, isActive: boolean) => {
    try {
      await axios.put(`${API_BASE_URL}/api/timers/${timer.id}`, { is_active: isActive });
      await fetchTimers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update timer'));
    }
  };

  const resetTimer = async (timer: Timer) => {
    try {
      await axios.post(`${API_BASE_URL}/api/timers/${timer.id}/reset`);
      await refreshTimers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to reset timer'));
    }
  };

  // Archive is reversible, so no confirmation dialog (delete keeps its
  // "cannot be undone" confirmation). The refetch drops the timer from the
  // default listing; it stays reachable via the Archived state filter.
  const archiveTimer = async (timer: Timer) => {
    try {
      await axios.post(`${API_BASE_URL}/api/timers/${timer.id}/archive`);
      await fetchTimers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to archive timer'));
    }
  };

  const restoreTimer = async (timer: Timer) => {
    try {
      await axios.post(`${API_BASE_URL}/api/timers/${timer.id}/restore`);
      await fetchTimers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to restore timer'));
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedTags([]);
    setRecurrenceFilter([]);
    setStateFilter([]);
    setCreatedAfter('');
    setCreatedBefore('');
    setOlderThanDays('');
    setMinDurationHours('');
    setMaxDurationHours('');
  };

  const renderActions = (timer: Timer) => (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ minWidth: 176 }}>
      {timer.archived_at ? (
        // Archived rows drop the run controls; Restore is the one-click
        // way back into rotation.
        <Tooltip title="Restore">
          <IconButton size="small" color="primary" onClick={() => restoreTimer(timer)} aria-label="restore timer">
            <UnarchiveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <>
          {!timer.is_active ? (
            <Tooltip title="Start">
              <IconButton size="small" onClick={() => updateTimerActive(timer, true)} aria-label="start timer">
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Pause">
              <IconButton size="small" onClick={() => updateTimerActive(timer, false)} aria-label="pause timer">
                <PauseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Reset">
            <IconButton size="small" onClick={() => resetTimer(timer)} aria-label="reset timer">
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Archive">
            <IconButton size="small" onClick={() => archiveTimer(timer)} aria-label="archive timer">
              <ArchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="Edit">
        <IconButton size="small" onClick={() => handleEditClick(timer)} aria-label="edit timer">
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" color="error" onClick={() => handleDeleteClick(timer)} aria-label="delete timer">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  if (loading && timers.length === 0) {
    return (
      <PageShell maxWidth="wide">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="wide">
      <Box sx={pageHeaderSx}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="h3" component="h1">
              Countdowns
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Count-ups, countdowns, and recurring reminders for the dates that matter
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Tooltip title="Duration display options">
              <IconButton
                onClick={() => setDurationDialogOpen(true)}
                aria-label="duration display options"
              >
                <TuneIcon />
              </IconButton>
            </Tooltip>
            <ViewModeToggle
              value={viewMode}
              onChange={setViewMode}
              ariaLabel="timer view mode"
              isOverride={viewModeControls.isOverride}
              onUseDefault={viewModeControls.useDefault}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
            >
              Add Timer
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TimerViewsBar
        views={display.saved_views}
        activeViewId={activeViewId}
        defaultViewId={display.default_view_id}
        modified={viewModified}
        onSelect={applyView}
        onShowAll={showAllTimers}
        onSaveChanges={handleSaveViewChanges}
        onRevert={handleRevertView}
        onSaveAs={handleSaveViewAs}
        onDelete={handleDeleteView}
        onSetDefault={handleSetDefaultView}
      />

      <CollapsibleFilterBar panelKey="timers_filter_bar" activeFilterCount={activeFilterCount}>
        <Grid container spacing={pageGridSpacing} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="Search"
              fullWidth
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete
                multiple
                freeSolo
                size="small"
                sx={{ flexGrow: 1 }}
                options={tagOptions}
                value={selectedTags}
                onChange={(_, value) => setSelectedTags(value.map((tag) => tag.trim().toLowerCase()).filter(Boolean))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Tags" />}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={effectiveTagMode}
                onChange={(_, value) => handleTagModeChange(value as TagFilterMode | null)}
                aria-label="tag filter mode"
              >
                <ToggleButton value="any" aria-label="match any tag">
                  Any
                </ToggleButton>
                <ToggleButton value="all" aria-label="match all tags">
                  All
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Recurrence</InputLabel>
              <Select
                multiple
                value={recurrenceFilter}
                label="Recurrence"
                onChange={(e) => setRecurrenceFilter(parseMultiSelect(e.target.value as string | string[]))}
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {recurrenceOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              {/* Explicit ids wire aria-labelledby on the Select trigger so
                  the control has an accessible name ("State"). */}
              <InputLabel id="timer-state-filter-label">State</InputLabel>
              <Select
                multiple
                labelId="timer-state-filter-label"
                value={stateFilter}
                label="State"
                onChange={(e) => setStateFilter(parseMultiSelect(e.target.value as string | string[]))}
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {stateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Older Than</InputLabel>
              <Select
                value={olderThanDays}
                label="Older Than"
                onChange={(e) => setOlderThanDays(e.target.value)}
              >
                <MenuItem value="">Any age</MenuItem>
                <MenuItem value="30">30 days</MenuItem>
                <MenuItem value="90">90 days</MenuItem>
                <MenuItem value="180">180 days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Created After"
              type="date"
              size="small"
              fullWidth
              value={createdAfter}
              onChange={(e) => setCreatedAfter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Created Before"
              type="date"
              size="small"
              fullWidth
              value={createdBefore}
              onChange={(e) => setCreatedBefore(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              label="Min Hours"
              type="number"
              size="small"
              fullWidth
              value={minDurationHours}
              onChange={(e) => setMinDurationHours(e.target.value)}
              inputProps={{ min: 0, step: 0.25 }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              label="Max Hours"
              type="number"
              size="small"
              fullWidth
              value={maxDurationHours}
              onChange={(e) => setMaxDurationHours(e.target.value)}
              inputProps={{ min: 0, step: 0.25 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as SortBy)}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              >
                <MenuItem value="asc">Asc</MenuItem>
                <MenuItem value="desc">Desc</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!hasFilters}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </CollapsibleFilterBar>

      {timers.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: { xs: 5, md: 6 },
            px: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {hasFilters ? 'No timers match your filters' : 'No timers yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {hasFilters
              ? 'Adjust the filters or clear them to see more timers'
              : 'Create your first timer to track countdowns or elapsed time'}
          </Typography>
          {hasFilters ? (
            <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters}>
              Clear Filters
            </Button>
          ) : (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
              Add Your First Timer
            </Button>
          )}
        </Box>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={pageGridSpacing}>
          {timers.map((timer) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={timer.id}>
              <TimerCard
                timer={timer}
                now={now}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onStart={(item) => updateTimerActive(item, true)}
                onPause={(item) => updateTimerActive(item, false)}
                onReset={resetTimer}
                onArchive={archiveTimer}
                onRestore={restoreTimer}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table
            size="small"
            aria-label="timers list"
            sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Timer</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Recurrence</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Next / Reset</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timers.map((timer) => {
                const displayTarget = getTimerDisplayTarget(timer, now);
                return (
                  <TableRow key={timer.id} hover>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography variant="subtitle2">{timer.title}</Typography>
                      {timer.description && (
                        <Typography variant="caption" color="text.secondary">
                          {timer.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatTimerDuration(timer, now)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={timer.state || (timer.is_active ? 'active' : 'paused')}
                        size="small"
                        color={stateColor(timer.state) as any}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {timer.tags.length ? timer.tags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        )) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatRecurrence(timer)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(timer.created_at)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 190 }}>
                      <Typography variant="caption" display="block">
                        {timer.timer_type === 'countdown' ? formatDateTime(displayTarget.toISOString()) : '-'}
                      </Typography>
                      {timer.last_reset_at && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Reset {formatDateTime(timer.last_reset_at)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {renderActions(timer)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddTimerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveTimer}
        editTimer={editTimer}
      />

      <DurationDisplayDialog
        open={durationDialogOpen}
        onClose={() => setDurationDialogOpen(false)}
        settings={display}
        onChange={writeDisplay}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Timer</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText>
            Are you sure you want to delete "{timerToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Timers;
